import type { Shortcut } from '@/hooks/use-keyboard-shortcuts';
import { newIdempotencyKey } from '@/lib/idempotency';
import type { trpc } from '@/lib/trpc';
import type { OpportunityRow } from '@tennis-scanner/shared';
import { type Dispatch, type SetStateAction, useCallback, useMemo } from 'react';
import type { NavigateFunction } from 'react-router-dom';

const SNOOZE_MINUTES_HOTKEY = 60;

interface Args {
  rows: OpportunityRow[];
  focused: OpportunityRow | null;
  setFocusIdx: Dispatch<SetStateAction<number>>;
  setExecuting: Dispatch<SetStateAction<OpportunityRow | null>>;
  setHelpOpen: Dispatch<SetStateAction<boolean>>;
  scrollFocusIntoView: (idx: number) => void;
  navigate: NavigateFunction;
  snooze: ReturnType<typeof trpc.opportunities.snooze.useMutation>;
  markPassed: ReturnType<typeof trpc.opportunities.markPassed.useMutation>;
}

// Builds the shortcut bindings for the dashboard. Lives in its own file so
// dashboard.tsx stays under the 300 LOC limit. j/k navigate, Enter opens
// the cockpit, e/p/s = execute/descartar/pausar, ? = help.
export function useDashboardShortcuts(args: Args): Shortcut[] {
  const {
    rows,
    focused,
    setFocusIdx,
    setExecuting,
    setHelpOpen,
    scrollFocusIntoView,
    navigate,
    snooze,
    markPassed,
  } = args;

  const moveDown = useCallback(() => {
    if (rows.length === 0) return;
    setFocusIdx((i) => {
      const next = Math.min(i + 1, rows.length - 1);
      scrollFocusIntoView(next);
      return next;
    });
  }, [rows.length, setFocusIdx, scrollFocusIntoView]);

  const moveUp = useCallback(() => {
    if (rows.length === 0) return;
    setFocusIdx((i) => {
      const next = Math.max(i - 1, 0);
      scrollFocusIntoView(next);
      return next;
    });
  }, [rows.length, setFocusIdx, scrollFocusIntoView]);

  return useMemo<Shortcut[]>(
    () => [
      { key: 'j', description: 'Bajar a la siguiente alerta', handler: moveDown },
      { key: 'k', description: 'Subir a la alerta anterior', handler: moveUp },
      {
        key: 'Enter',
        description: 'Abrir partido de la alerta enfocada',
        handler: () => {
          if (focused) navigate(`/matches/${focused.canonicalMatchId}`);
        },
      },
      {
        key: 'e',
        description: 'Ejecutar (abre confirmación)',
        handler: () => {
          if (focused) setExecuting(focused);
        },
      },
      {
        key: 'p',
        description: 'Descartar la alerta enfocada',
        handler: () => {
          if (!focused) return;
          markPassed.mutate({
            opportunityId: focused.id,
            idempotencyKey: newIdempotencyKey(),
          });
        },
      },
      {
        key: 's',
        description: 'Pausar 1h la alerta enfocada',
        handler: () => {
          if (!focused) return;
          snooze.mutate({
            opportunityId: focused.id,
            untilMinutes: SNOOZE_MINUTES_HOTKEY,
            idempotencyKey: newIdempotencyKey(),
          });
        },
      },
      {
        key: '?',
        description: 'Mostrar esta ayuda',
        handler: () => setHelpOpen((v) => !v),
      },
    ],
    [moveDown, moveUp, focused, navigate, snooze, markPassed, setExecuting, setHelpOpen],
  );
}
